/**
 * 003 — Bankbuchungen (US2, FR-UI-001): every movement of the period with its
 * status, the top match suggestion (confidence badge + expandable reasons) and
 * the review actions. Suggestions are only proposals: "Übernehmen" opens the
 * evidence-backed ReviewEditor prefilled from the suggestion, the bulk action
 * confirms selected "hoch" suggestions (one review entry per movement, C4),
 * "Ablehnen" stores a reason. Nothing is ever confirmed without a click.
 */
import { useId, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, EmptyState, FilterPill, GlassPanel, Input, LoadingSpinner, Modal, StatusBadge, SURFACE, type StatusTone } from '../ui';
import { ApiError } from '../../services/api';
import {
  CLASSIFICATION_LABELS,
  STATUS_LABELS,
  bankApi,
  bankKeys,
  reviewSideEffectKeys,
  day,
  money,
  type MovementStatus,
  type Suggestion,
  type TransactionRow,
  type TransactionsResponse,
} from '../../lib/bank-api';
import { useCashflow } from './OverviewTab';
import { ReviewEditor, errorText } from './ReviewEditor';
import { SuggestionBadge } from './SuggestionBadge';
import { SuggestionReasons, documentHref } from './SuggestionReasons';
import type { BankTabProps, FlowDocument } from './types';

type Filter = 'alle' | MovementStatus;
type Partners = TransactionsResponse['partners'];
type BulkItem = { id: string; expectedVersion: number; suggestionId: string };

const STATUSES: MovementStatus[] = ['offen', 'vorschlag', 'bestaetigt', 'ausgeblendet'];
const STATUS_TONES: Record<MovementStatus, StatusTone> = {
  offen: 'warning',
  vorschlag: 'info',
  bestaetigt: 'success',
  ausgeblendet: 'muted',
};
const KIND_LABELS: Record<Suggestion['kind'], string> = {
  document: 'Beleg',
  collective: 'Sammelzahlung',
  partial: 'Teilzahlung',
  classification: 'Klassifikation',
  debit_return_pair: 'Rücklastschrift-Paar',
};
/** Classifications a rule cannot produce (validation) or that need a document. */
const NO_RULE_CLASSIFICATIONS = ['business', 'unknown', 'debit_return'];
const REFERENCE_PREVIEW = 80;
const PAGE_SIZE = 100;
/** Contract limit of POST /transactions/review-bulk. */
const BULK_MAX = 100;

const classLabel = (c: string) => (CLASSIFICATION_LABELS as Record<string, string>)[c] ?? c;
const signed = (cents: number) => (cents > 0 ? `+${money(cents)}` : money(cents));
const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();

const counterpartyName = (row: Pick<TransactionRow, 'counterparty' | 'description'>) =>
  row.counterparty.name?.trim() || row.description.split('\n')[0]?.trim() || 'Unbekannte Gegenpartei';

const topSuggestion = (row: TransactionRow): Suggestion | undefined => row.suggestions[0];
const isBulkEligible = (row: TransactionRow) => {
  const top = topSuggestion(row);
  return Boolean(top && top.bulkEligible && top.level === 'hoch');
};

function suggestionTitle(s: Suggestion, partners: Partners): string {
  if (s.kind === 'debit_return_pair') return KIND_LABELS.debit_return_pair;
  if (s.kind === 'classification') {
    const partner = s.partnerId ? partners.find((p) => p.id === s.partnerId)?.displayName : undefined;
    return [classLabel(s.classification ?? 'unknown'), partner].filter(Boolean).join(' · ');
  }
  const targets = s.targets.map((t) => t.label).join(', ');
  return s.kind === 'document' ? targets : `${KIND_LABELS[s.kind]}: ${targets}`;
}

/** Body for "Regel daraus erstellen", or null when no valid rule follows from the row. */
function ruleFromRow(row: TransactionRow): Record<string, unknown> | null {
  if (row.status !== 'bestaetigt' || NO_RULE_CLASSIFICATIONS.includes(row.classification)) return null;
  const iban = row.counterparty.iban;
  const name = row.counterparty.name?.trim() ?? '';
  if (!iban && name.length < 2) return null;
  return {
    name: `${counterpartyName(row)} → ${classLabel(row.classification)}`.slice(0, 120),
    ...(iban ? { counterpartyIban: iban } : { counterpartyNameContains: name.slice(0, 120) }),
    direction: row.amountCents < 0 ? 'out' : 'in',
    outcomeClassification: row.classification,
    ...(row.partnerId ? { outcomePartnerId: row.partnerId } : {}),
    createdFromTransactionId: row.id,
  };
}

function SuggestionBlock({
  suggestion,
  partners,
  onAccept,
  onReject,
}: {
  suggestion: Suggestion;
  partners: Partners;
  onAccept: () => void;
  onReject: () => void;
}) {
  const titleId = useId();
  return (
    <div className={`${SURFACE.chip} space-y-2 rounded-lg p-3`} data-testid="bank-suggestion">
      <div className="flex flex-wrap items-center gap-2">
        <SuggestionBadge level={suggestion.level} />
        <span id={titleId} className="min-w-0 break-words text-sm text-white/90">
          {suggestionTitle(suggestion, partners)}
        </span>
      </div>
      <SuggestionReasons suggestion={suggestion} />
      <div className="bank-no-print flex flex-wrap gap-2">
        <Button size="sm" onClick={onAccept} aria-describedby={titleId}>
          Übernehmen
        </Button>
        <Button size="sm" variant="secondary" onClick={onReject} aria-describedby={titleId}>
          Ablehnen
        </Button>
      </div>
    </div>
  );
}

function MovementCard({
  row,
  documents,
  partners,
  selectable,
  selected,
  onSelect,
  onAccept,
  onReject,
  onManual,
  rule,
  editor,
}: {
  row: TransactionRow;
  documents: FlowDocument[];
  partners: Partners;
  selectable: boolean;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  onAccept: (s: Suggestion) => void;
  onReject: (s: Suggestion) => void;
  onManual: () => void;
  rule: { available: boolean; pending: boolean; created: boolean; error: string | null; onCreate: () => void };
  editor: ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const name = counterpartyName(row);
  const reference = oneLine(row.counterparty.reference || row.description);
  const preview = reference.length > REFERENCE_PREVIEW ? `${reference.slice(0, REFERENCE_PREVIEW).trimEnd()}…` : reference;
  const hasMore = preview !== oneLine(row.description);
  const [top, ...rest] = row.suggestions;
  const partner = row.partnerId ? partners.find((p) => p.id === row.partnerId)?.displayName : undefined;
  const documentNumber = (kind: 'invoice' | 'expense', id: string) =>
    documents.find((d) => d.id === id && d.kind === kind)?.number ?? id;
  const allocationSummary = row.allocations.length
    ? `${row.allocations.length} ${row.allocations.length === 1 ? 'Zuordnung' : 'Zuordnungen'} · offener Belegrest ${money(row.unallocatedCents)}`
    : row.status === 'bestaetigt'
      ? 'Ohne Belegzuordnung'
      : 'Zuordnung offen';
  return (
    <article className="p-4" data-testid="bank-movement" aria-label={`${day(row.bookingDate)} · ${name} · ${signed(row.amountCents)}`}>
      <div className="flex items-start gap-3">
        {selectable && (
          <input
            type="checkbox"
            className="bank-no-print mt-1 h-5 w-5 shrink-0 cursor-pointer"
            checked={selected}
            onChange={(e) => onSelect(e.target.checked)}
            aria-label={`Für Sammelbestätigung auswählen: ${name}, ${signed(row.amountCents)}`}
            data-testid="bulk-select-row"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <div className="min-w-0">
              <div className="text-sm text-white/50">
                {day(row.bookingDate)}
                {row.valueDate !== row.bookingDate && <> · Valuta {day(row.valueDate)}</>}
              </div>
              <h3 className="break-words font-medium text-white">{name}</h3>
            </div>
            <strong
              className={`whitespace-nowrap tabular-nums ${row.amountCents > 0 ? 'text-emerald-200' : 'text-white'}`}
              data-testid="bank-movement-amount"
            >
              {signed(row.amountCents)}
            </strong>
          </div>
          <div className="mt-1 text-sm text-white/70">
            {expanded ? (
              <p className="whitespace-pre-line break-words">{row.description}</p>
            ) : (
              <p className="break-words">{preview}</p>
            )}
            {hasMore && (
              <button
                type="button"
                className="bank-no-print mt-1 inline-flex min-h-11 items-center text-xs text-white/60 underline decoration-white/30 hover:text-white sm:min-h-0"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? 'Weniger anzeigen' : 'Vollständigen Buchungstext anzeigen'}
              </button>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <StatusBadge tone={STATUS_TONES[row.status]} data-testid="bank-movement-status">
              {STATUS_LABELS[row.status]}
            </StatusBadge>
            {row.status === 'bestaetigt' && (
              <span className="text-white/80" data-testid="bank-movement-classification">
                {classLabel(row.classification)}
                {partner ? ` · ${partner}` : ''}
              </span>
            )}
            <span className="text-white/50">{allocationSummary}</span>
          </div>
          {(row.inactiveAllocations?.length ?? 0) > 0 && (
            <p role="status" className="mt-2 rounded border border-amber-400/40 p-2 text-sm text-amber-200" data-testid="bank-inactive-allocation">
              {row.inactiveAllocations!.length === 1 ? 'Ein zugeordneter Beleg wurde' : `${row.inactiveAllocations!.length} zugeordnete Belege wurden`}{' '}
              zusammengeführt oder zurückgezogen ({row.inactiveAllocations!.map((a) => money(a.amountCents)).join(', ')}). Die
              Zuordnung zählt nicht mehr — bitte neu zuordnen; die Prüfhistorie bleibt erhalten.
            </p>
          )}
          {(row.directionMismatchAllocations?.length ?? 0) > 0 && (
            <p role="status" className="mt-2 rounded border border-amber-400/40 p-2 text-sm text-amber-200" data-testid="bank-direction-mismatch">
              Der Beleg wurde inzwischen korrigiert (z. B. Rechnung → Gutschrift); die Zahlungsrichtung passt nicht mehr zur
              bestätigten Zuordnung ({row.directionMismatchAllocations!.map((a) => money(a.amountCents)).join(', ')}). Die Zuordnung
              zählt nicht mehr — bitte neu prüfen; die Prüfhistorie bleibt erhalten.
            </p>
          )}
          {row.allocations.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm">
              {row.allocations.map((a, i) => (
                <li key={i} className="break-words text-white/70">
                  <a
                    href={documentHref(a.kind, a.documentId)}
                    target="_blank"
                    rel="noreferrer"
                    title="Beleg in neuem Tab öffnen"
                    className="text-white/90 underline decoration-white/30 hover:decoration-white"
                  >
                    {a.kind === 'invoice' ? 'Rechnung' : 'Ausgabe'} {documentNumber(a.kind, a.documentId)}
                  </a>
                  : {money(a.amountCents)} · {a.evidence}
                </li>
              ))}
            </ul>
          )}
          {row.status === 'bestaetigt' && row.reviewEvidence && (
            <p className="mt-1 break-words text-sm text-white/60">Evidenz: {row.reviewEvidence}</p>
          )}
          {top && (
            <div className="mt-3 space-y-2" data-testid="bank-top-suggestion">
              <SuggestionBlock
                suggestion={top}
                partners={partners}
                onAccept={() => onAccept(top)}
                onReject={() => onReject(top)}
              />
              {rest.length > 0 && (
                <details>
                  <summary className="cursor-pointer text-sm text-white/70">Weitere Vorschläge ({rest.length})</summary>
                  <div className="mt-2 space-y-2">
                    {rest.map((s) => (
                      <SuggestionBlock
                        key={s.id}
                        suggestion={s}
                        partners={partners}
                        onAccept={() => onAccept(s)}
                        onReject={() => onReject(s)}
                      />
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
          {row.reviewHistory.length > 0 && (
            <details className="mt-3 text-sm" data-testid="bank-review-history">
              <summary className="cursor-pointer text-white/70">Prüfhistorie ({row.reviewHistory.length})</summary>
              <ol className="mt-2 space-y-1 border-l border-white/10 pl-3">
                {row.reviewHistory.map((h, i) => (
                  <li key={i} className="break-words text-white/80">
                    {day(h.at)} · {h.actor} · {classLabel(h.classification)} · {h.evidence}
                    {h.suggestion && <span className="text-white/50"> · Vorschlag ({h.suggestion.level})</span>}
                    {h.bulk && (
                      <>
                        {' '}
                        <StatusBadge tone="neutral">Sammelbestätigung</StatusBadge>
                      </>
                    )}
                  </li>
                ))}
              </ol>
            </details>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <a
              href={bankApi.originalUrl(row.statementId)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 items-center text-white/80 underline decoration-white/30 hover:decoration-white sm:min-h-8"
            >
              Originalauszug
              {row.statementPeriod ? ` ${day(row.statementPeriod.from)} – ${day(row.statementPeriod.to)}` : ''}
            </a>
            <Button size="sm" variant="secondary" className="bank-no-print" onClick={onManual}>
              Manuell prüfen
            </Button>
            {rule.available && (
              <Button
                size="sm"
                variant="secondary"
                className="bank-no-print"
                disabled={rule.created}
                loading={rule.pending}
                onClick={rule.onCreate}
              >
                {rule.created ? 'Regel gespeichert' : 'Regel daraus erstellen'}
              </Button>
            )}
          </div>
          {rule.error && (
            <p role="alert" className="mt-2 text-sm text-red-300">
              {rule.error}
            </p>
          )}
          {editor && <div className="mt-3">{editor}</div>}
        </div>
      </div>
    </article>
  );
}

function RejectDialog({
  row,
  suggestion,
  partners,
  onClose,
  onDone,
}: {
  row: TransactionRow;
  suggestion: Suggestion;
  partners: Partners;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const client = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const reject = useMutation({
    mutationFn: () => bankApi.reject(row.id, suggestion.id, reason.trim()),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: bankKeys.all });
      onDone('Vorschlag abgelehnt.');
    },
    onError: (e) => {
      if (e instanceof ApiError && (e.status === 404 || e.status === 409)) {
        void client.invalidateQueries({ queryKey: bankKeys.all });
        setError(
          e.status === 404 ? 'Vorschlag nicht mehr aktuell; die Ansicht wurde aktualisiert.' : `${errorText(e)} Die Ansicht wurde aktualisiert.`
        );
      } else setError(errorText(e));
    },
  });
  const invalid = reason.trim().length < 3 || reject.isPending;
  return (
    <Modal isOpen onClose={onClose} title="Vorschlag ablehnen">
      <form
        className="space-y-4"
        data-testid="bank-reject-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!invalid) reject.mutate();
        }}
      >
        <div className="space-y-1 text-sm">
          <p className="text-white/70">
            {day(row.bookingDate)} · {counterpartyName(row)} · {signed(row.amountCents)}
          </p>
          <p className="flex flex-wrap items-center gap-2 text-white/90">
            <SuggestionBadge level={suggestion.level} />
            <span className="min-w-0 break-words">{suggestionTitle(suggestion, partners)}</span>
          </p>
        </div>
        <Input
          label="Begründung"
          name="reason"
          value={reason}
          maxLength={2000}
          onChange={(e) => setReason(e.target.value)}
          hint="Mindestens 3 Zeichen. Der Vorschlag erscheint erst wieder, wenn sich Betrag, Nummer oder Gegenpartei des Belegs ändern."
        />
        {error && (
          <p role="alert" className="text-sm text-red-300">
            {error}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="danger" disabled={invalid} loading={reject.isPending}>
            Ablehnen
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function TransactionsTab(props: BankTabProps) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: bankKeys.transactions(props.tenantId, props.from, props.to, props.account),
    queryFn: () => bankApi.transactions({ from: props.from, to: props.to, account: props.account }),
    enabled: Boolean(props.tenantId),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
  });
  // Same key as the Übersicht tab (cached): documents for the editor and allocation labels.
  const cashflow = useCashflow(props);
  const documents = cashflow.data?.eligibleDocuments ?? [];
  const [filter, setFilter] = useState<Filter>('alle');
  const [limit, setLimit] = useState(PAGE_SIZE);
  // Bulk selection keeps the version and suggestion the reviewer SAW: after a refresh the
  // server answers 409 instead of confirming a different (newer) suggestion.
  const [selected, setSelected] = useState<ReadonlyMap<string, BulkItem>>(() => new Map());
  // Snapshot of the row as it was when the editor opened: its version is the optimistic lock the
  // save sends, so a reload while the editor is open must not swap in a newer row (409 stays a 409).
  const [editing, setEditing] = useState<{ row: TransactionRow; suggestion: Suggestion | null } | null>(null);
  const [rejecting, setRejecting] = useState<{ row: TransactionRow; suggestion: Suggestion } | null>(null);
  const [bulkError, setBulkError] = useState('');
  const [ruleError, setRuleError] = useState<{ id: string; message: string } | null>(null);
  const [rulesCreated, setRulesCreated] = useState<ReadonlySet<string>>(() => new Set());
  // A bulk selection belongs to the scope it was made in: a new tenant, account or period clears it,
  // while ordinary refetches keep the captured versions (Codex #15).
  const scope = `${props.tenantId}|${props.from}|${props.to}|${props.account ?? ''}`;
  const [selectionScope, setSelectionScope] = useState(scope);
  if (selectionScope !== scope) {
    setSelectionScope(scope);
    setSelected(new Map());
  }
  const invalidate = () => void client.invalidateQueries({ queryKey: bankKeys.all });

  const bulk = useMutation({
    mutationFn: (items: BulkItem[]) => bankApi.bulkReview(items),
    onSuccess: (result, items) => {
      for (const queryKey of reviewSideEffectKeys) void client.invalidateQueries({ queryKey });
      setSelected(new Map());
      setBulkError('');
      const n = result?.confirmed ?? items.length;
      props.onNotice(`${n} ${n === 1 ? 'Buchung' : 'Buchungen'} bestätigt.`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        invalidate();
        setSelected(new Map());
        setBulkError(`${errorText(e)} Die Ansicht wurde aktualisiert.`);
      } else setBulkError(errorText(e));
    },
  });
  const createRule = useMutation({
    mutationFn: (row: TransactionRow) => {
      const body = ruleFromRow(row);
      if (!body) throw new Error('Aus dieser Buchung lässt sich keine Regel ableiten.');
      return bankApi.createRule(body);
    },
    onSuccess: (_rule, row) => {
      invalidate();
      setRuleError(null);
      setRulesCreated((s) => new Set(s).add(row.id));
      props.onNotice('Regel gespeichert.');
    },
    onError: (e, row) => setRuleError({ id: row.id, message: errorText(e) }),
  });

  if (query.isLoading) return <LoadingSpinner />;
  if (query.error)
    return (
      <p role="alert" className="rounded border border-red-400/40 p-3">
        {errorText(query.error)}
      </p>
    );
  const data = query.data;
  if (!data) return null;

  const rows = data.rows;
  const filtered = filter === 'alle' ? rows : rows.filter((r) => r.status === filter);
  const shown = filtered.slice(0, limit);
  const eligibleShown = shown.filter(isBulkEligible);
  const snapshot = (r: TransactionRow): BulkItem | null => {
    const top = topSuggestion(r);
    return top ? { id: r.id, expectedVersion: r.version, suggestionId: top.id } : null;
  };
  const selectedItems: BulkItem[] = [...selected.values()];
  const total = STATUSES.reduce((sum, s) => sum + data.counts[s], 0);
  const pairCandidates = (row: TransactionRow) =>
    rows
      // The existing counterpart of a confirmed pair stays selectable (amend evidence without unpairing).
      // A return is booked on the account the debit left; other accounts are never a valid pair (Codex #15).
      .filter(
        (r) =>
          r.id !== row.id &&
          r.account === row.account &&
          r.amountCents === -row.amountCents &&
          (r.status !== 'bestaetigt' || r.id === row.pairedTransactionId)
      )
      .map((r) => ({ id: r.id, bookingDate: r.bookingDate, amountCents: r.amountCents, label: counterpartyName(r) }));
  const chooseFilter = (f: Filter) => {
    setFilter(f);
    setLimit(PAGE_SIZE);
  };
  const toggle = (row: TransactionRow, checked: boolean) =>
    setSelected((s) => {
      const next = new Map(s);
      const item = snapshot(row);
      if (checked && item) next.set(row.id, item);
      else next.delete(row.id);
      return next;
    });

  return (
    <div className="space-y-6">
      <GlassPanel className="bank-no-print">
        <div className="flex flex-col gap-4">
          <div role="group" aria-label="Nach Status filtern" data-testid="bank-status-filter" className="flex flex-wrap gap-2">
            {(['alle', ...STATUSES] as const).map((f) => (
              <FilterPill key={f} active={filter === f} onClick={() => chooseFilter(f)} className="min-h-11 sm:min-h-8">
                {f === 'alle' ? 'Alle' : STATUS_LABELS[f]}{' '}
                <span data-testid={`bank-status-count-${f}`} className={filter === f ? 'text-white/60' : 'text-white/40'}>
                  {f === 'alle' ? total : data.counts[f]}
                </span>
              </FilterPill>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="bank-bulk-bar">
            <Button
              size="sm"
              variant="secondary"
              data-testid="bulk-select-hoch"
              disabled={eligibleShown.length === 0}
              onClick={() =>
                setSelected(
                  new Map(
                    eligibleShown
                      .slice(0, BULK_MAX)
                      .map((r) => [r.id, snapshot(r)] as const)
                      .filter((e): e is readonly [string, BulkItem] => e[1] !== null)
                  )
                )
              }
            >
              Alle „hoch“ auswählen ({eligibleShown.length})
            </Button>
            {selectedItems.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Map())}>
                Auswahl aufheben
              </Button>
            )}
            <Button
              size="sm"
              disabled={selectedItems.length === 0 || selectedItems.length > BULK_MAX}
              loading={bulk.isPending}
              onClick={() => {
                setBulkError('');
                bulk.mutate(selectedItems);
              }}
            >
              Ausgewählte übernehmen ({selectedItems.length})
            </Button>
          </div>
          <p className="text-xs text-white/50">
            Sammelbestätigung nur für eindeutige Vorschläge mit Konfidenz „hoch“. Jede Buchung erhält einen eigenen
            Prüfeintrag; nichts wird ohne Ihren Klick bestätigt.
            {selectedItems.length > BULK_MAX && ` Höchstens ${BULK_MAX} Buchungen je Sammelbestätigung.`}
          </p>
          {bulkError && (
            <p role="alert" className="text-sm text-red-300">
              {bulkError}
            </p>
          )}
        </div>
      </GlassPanel>
      {rows.length === 0 ? (
        <EmptyState title="Keine Bankbuchungen im Zeitraum." description="Zeitraum oder Konto anpassen oder Originalauszüge importieren." />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={`Keine Buchungen mit Status „${filter === 'alle' ? '' : STATUS_LABELS[filter]}“.`}
          description="Filter anpassen."
        />
      ) : (
        <GlassPanel variant="subtle" padding="none" className="overflow-hidden" data-testid="bank-transactions-list">
          <ul className="divide-y divide-white/10">
            {shown.map((row) => (
              <li key={row.id}>
                <MovementCard
                  row={row}
                  documents={documents}
                  partners={data.partners}
                  selectable={isBulkEligible(row)}
                  selected={selected.has(row.id)}
                  onSelect={(checked) => toggle(row, checked)}
                  onAccept={(suggestion) => setEditing({ row, suggestion })}
                  onReject={(suggestion) => setRejecting({ row, suggestion })}
                  onManual={() => setEditing({ row, suggestion: null })}
                  rule={{
                    available: props.canManage && ruleFromRow(row) !== null,
                    pending: createRule.isPending && createRule.variables?.id === row.id,
                    created: rulesCreated.has(row.id),
                    error: ruleError?.id === row.id ? ruleError.message : null,
                    onCreate: () => createRule.mutate(row),
                  }}
                  editor={
                    editing?.row.id === row.id ? (
                      <ReviewEditor
                        key={`${editing.row.id}:${editing.row.version}:${editing.suggestion?.id ?? 'manuell'}`}
                        target={editing.row}
                        suggestion={editing.suggestion}
                        documents={documents}
                        partners={data.partners}
                        pairCandidates={pairCandidates(editing.row)}
                        onClose={() => setEditing(null)}
                        onSaved={(message) => {
                          setEditing(null);
                          props.onNotice(message);
                        }}
                      />
                    ) : null
                  }
                />
              </li>
            ))}
          </ul>
          {filtered.length > shown.length && (
            <div className="border-t border-white/10 p-4">
              <Button variant="secondary" onClick={() => setLimit((l) => l + PAGE_SIZE)}>
                Weitere {Math.min(PAGE_SIZE, filtered.length - shown.length)} anzeigen ({filtered.length - shown.length}{' '}
                übrig)
              </Button>
            </div>
          )}
        </GlassPanel>
      )}
      {rejecting && (
        <RejectDialog
          key={`${rejecting.row.id}:${rejecting.suggestion.id}`}
          row={rejecting.row}
          suggestion={rejecting.suggestion}
          partners={data.partners}
          onClose={() => setRejecting(null)}
          onDone={(message) => {
            setRejecting(null);
            props.onNotice(message);
          }}
        />
      )}
    </div>
  );
}
